import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddInventoryDialog from './Popups/AddInventory';
import AddEquipmentDialog from './Popups/AddEquipment';
import AddMachineDialog from './Popups/AddMachine';
import MachineImport from './MachineImport';
import MachineExport from './MachineExport';
import EquipmentImport from './EquipmentImport';
import EquipmentExport from './EquipmentExport';
import InventoryImport from './InventoryImport';
import InventoryExport from './InventoryExport';
import AddWorkbenchDialog from './Popups/AddWorkbench';
import BarcodeScanner from './Barcode/BarcodeScanner';
import { useSelector } from 'react-redux';
import MachineListAPIs from '../../utilities/api/machine/AdminMachineList';
import EquipmentListAPIs from '../../utilities/api/equipment/AdminEquipmentList';
import InventoryListAPIs from '../../utilities/api/inventory/AdminInventoryList';

const TopBarComponent = ({
  activeTab,
  setActiveTab,
  update,
  setUpdate,
  flag,
  setFlag,
  groups,
  machinesProp,
  equipmentsProp,
  inventoriesProp,
}) => {
  const user = useSelector(state => state.user.user);
  const categories = useSelector(state => state.category.category_list);
  const locations = useSelector(state => state.location.location_list);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isAddInventoryOpen, setAddInventoryOpen] = useState(false);
  const [isAddEquipmentOpen, setAddEquipmentOpen] = useState(false);
  const [isAddMachineOpen, setAddMachineOpen] = useState(false);
  const [isAddWorkbenchOpen, setAddWorkbenchOpen] = useState(false);
  const [isScannerOpen, setScannerOpen] = useState(false);

  /* Machines, equipments and inventories are now managed in Inventory.jsx 
     and passed down via props to avoid multiple API calls. */
  const [machines, setMachines] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [inventories, setInventories] = useState([]);

  useEffect(() => {
    setMachines(machinesProp || []);
    setEquipments(equipmentsProp || []);
    setInventories(inventoriesProp || []);
  }, [machinesProp, equipmentsProp, inventoriesProp]);

  const handleAddButtonClick = () => {
    switch (activeTab) {
      case 0:
        setAddMachineOpen(true);
        break;
      case 1:
        setAddEquipmentOpen(true);
        break;
      case 2:
        setAddInventoryOpen(true);
        break;
      case 3:
        setAddWorkbenchOpen(true);
        break;
      default:
        break;
    }
  };

  const handleClose = () => {
    setAddMachineOpen(false);
    setAddEquipmentOpen(false);
    setAddInventoryOpen(false);
    setAddWorkbenchOpen(false);
  };

  const handleChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 2, md: 3 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        {/* TABS */}
        <Box
          sx={{
            width: { xs: '100%', md: 'auto' },
            overflowX: 'auto',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
            sx={{
              background: '#F4F4F4',
              borderRadius: '12px',
              p: 1,
              minHeight: 'unset',
              '& .MuiTabs-flexContainer': {
                gap: '8px',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 40,
                px: { xs: 2, sm: 2 },
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                color: '#333',
                background: '#fff',
                border: '1px solid #E6E6E6',
                flex: 1,
              },
              '& .Mui-selected': {
                background: '#333',
                color: '#fff !important',
                border: '1px solid #333',
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tooltip
              title="Machines can be reserved by booking a free slot"
              arrow
            >
              <Tab label="Machines" />
            </Tooltip>
            <Tooltip
              title="Equipments do not require approval or reservations"
              arrow
            >
              <Tab label="Equipment" />
            </Tooltip>
            <Tooltip title="Inventory contains all shelvable items" arrow>
              <Tab label="Inventory" />
            </Tooltip>
          </Tabs>
        </Box>

        {/* RIGHT ACTION SECTION */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent={{ xs: 'center', sm: 'flex-end' }}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          {/* Import / Export */}
          <Stack
            direction="row"
            spacing={1}
            flexWrap="nowrap"
            justifyContent={{ xs: 'center', sm: 'flex-start' }}
            flex={{ xs: 1, md: 'none' }}
          >
            {activeTab === 0 && (
              <>
                <MachineExport
                  machines={machines}
                  categories={categories}
                  locations={locations}
                />
                <MachineImport
                  user={user}
                  categories={categories}
                  locations={locations}
                  onImportComplete={() => {
                    setUpdate(prev => !prev);
                    setFlag(prev => !prev);
                  }}
                />
              </>
            )}
            {activeTab === 1 && (
              <>
                <EquipmentExport
                  equipments={equipments}
                  categories={categories}
                  locations={locations}
                />
                <EquipmentImport
                  user={user}
                  categories={categories}
                  locations={locations}
                  onImportComplete={() => {
                    setUpdate(prev => !prev);
                    setFlag(prev => !prev);
                  }}
                />
              </>
            )}
            {activeTab === 2 && (
              <>
                <InventoryExport
                  inventories={inventories}
                  categories={categories}
                  locations={locations}
                />
                <InventoryImport
                  user={user}
                  categories={categories}
                  locations={locations}
                  groups={groups}
                  onImportComplete={() => {
                    setUpdate(prev => !prev);
                    setFlag(prev => !prev);
                  }}
                />
              </>
            )}
          </Stack>

          {/* ADD BUTTON */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddButtonClick}
            sx={{
              padding: '12px 16px',
              flex: { xs: 1, md: 'none' },
              background: '#007BFF',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { background: '#007BFF' },
              '& .MuiButton-startIcon': {
                margin: 0,
              },
            }}
          >
            <Typography  variant="subtitle1">
            {activeTab === 0
              ? 'Add Machine'
              : activeTab === 1
                ? 'Add Equipment'
                : activeTab === 2
                  ? 'Add Inventory'
                  : 'Add Workbench'}
            </Typography>
          </Button>
        </Stack>
      </Stack>

      {/* Scanner Dialog */}
      <Dialog
        open={isScannerOpen}
        onClose={() => setScannerOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Scan a Barcode</DialogTitle>
        <DialogContent>
          <BarcodeScanner onClose={() => setScannerOpen(false)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScannerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialogs */}
      <AddInventoryDialog
        open={isAddInventoryOpen}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
        onClose={handleClose}
        setScannerOpen={setScannerOpen}
      />
      <AddEquipmentDialog
        open={isAddEquipmentOpen}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
        onClose={handleClose}
        setScannerOpen={setScannerOpen}
      />
      <AddMachineDialog
        open={isAddMachineOpen}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
        onClose={handleClose}
        setScannerOpen={setScannerOpen}
      />
      <AddWorkbenchDialog
        open={isAddWorkbenchOpen}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
        onClose={handleClose}
      />
    </Box>
  );
};

export default TopBarComponent;
